import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";

/* ============================================================================
   FilterPopover — the single Excel-style funnel filter for the whole ERP.

   Sections (per column kind):
     • Sort A→Z / Z→A
     • Filter by condition  (logical operators)
         - text   : contains / not contains / equals / not equals /
                    begins with / ends with / blank / not blank
         - number : = / ≠ / > / ≥ / < / ≤ / between / blank / not blank
         - date   : = / ≥ / > / ≤ / < / between / blank / not blank
     • Filter by value  (checkbox list w/ search, Select-All, counts) — set kind

   Draft model: edits are local; nothing commits until OK / Apply (Excel-like).
   Closes on outside click / Esc. Anchored (fixed) to the header funnel button.
   ============================================================================ */

const TEXT_OPS = [
  { val: "contains",    label: "Contains" },
  { val: "notContains", label: "Does not contain" },
  { val: "equals",      label: "Equals" },
  { val: "notEquals",   label: "Does not equal" },
  { val: "beginsWith",  label: "Begins with" },
  { val: "endsWith",    label: "Ends with" },
  { val: "blank",       label: "Is blank" },
  { val: "notBlank",    label: "Is not blank" },
];
const NUM_OPS = [
  { val: "eq",       label: "= Equals" },
  { val: "ne",       label: "\u2260 Does not equal" },
  { val: "gt",       label: "> Greater than" },
  { val: "gte",      label: "\u2265 Greater or equal" },
  { val: "lt",       label: "< Less than" },
  { val: "lte",      label: "\u2264 Less or equal" },
  { val: "between",  label: "\u2194 Between" },
  { val: "blank",    label: "Is blank" },
  { val: "notBlank", label: "Is not blank" },
];
const DATE_OPS = [
  { val: "eq",       label: "= On" },
  { val: "gte",      label: "\u2265 On or after" },
  { val: "gt",       label: "> After" },
  { val: "lte",      label: "\u2264 On or before" },
  { val: "lt",       label: "< Before" },
  { val: "between",  label: "\u2194 Between" },
  { val: "blank",    label: "Is blank" },
  { val: "notBlank", label: "Is not blank" },
];

const FilterPopover = ({
  column,
  kind,                 // 'set' | 'date'
  valueType = "text",   // 'text' | 'number'  (set kind only)
  anchor,               // { top, left }
  values = [],          // [ [text, count], ... ]  (set kind)
  current,              // existing descriptor for this column
  onApply,              // (descriptor) => void
  onClear,              // () => void
  onSort,               // ('asc'|'desc') => void
  onClose,
}) => {
  const ref = useRef(null);
  const selAllRef = useRef(null);
  const isDate = kind === "date";
  const ops = isDate ? DATE_OPS : valueType === "number" ? NUM_OPS : TEXT_OPS;
  const inputType = isDate ? "date" : valueType === "number" ? "number" : "text";

  const allValues = useMemo(() => values.map(([v]) => v), [values]);

  const [q, setQ] = useState("");
  const [op, setOp] = useState(current?.type === "cond" || current?.type === "date" ? current.op : ops[0].val);
  const [v1, setV1] = useState(current?.type === "cond" || current?.type === "date" ? current.val1 || "" : "");
  const [v2, setV2] = useState(current?.type === "cond" || current?.type === "date" ? current.val2 || "" : "");
  const [working, setWorking] = useState(
    () => (current?.type === "set" ? new Set(current.set) : new Set(allValues))
  );

  const shown = useMemo(
    () => (q ? values.filter(([v]) => String(v).toLowerCase().includes(q.toLowerCase())) : values),
    [values, q]
  );
  const selectedCount = working.size;
  const allSelected = selectedCount === allValues.length;
  const noneSelected = selectedCount === 0;
  const someSelected = !allSelected && !noneSelected;
  const needsVal = op !== "blank" && op !== "notBlank";

  useEffect(() => { if (selAllRef.current) selAllRef.current.indeterminate = someSelected; }, [someSelected]);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const toggleValue = (v) => setWorking((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const toggleAll = (checked) => setWorking(checked ? new Set(allValues) : new Set());

  const applyCondition = () => {
    onApply(isDate
      ? { type: "date", op, val1: v1, val2: v2 }
      : { type: "cond", op, val1: v1, val2: v2, valueType });
    onClose();
  };
  const applyValues = () => {
    if (allSelected) onClear();
    else onApply({ type: "set", set: working });
    onClose();
  };

  // Keep the whole popover within the viewport: measure after render and
  // clamp/flip so the footer (Clear / OK) is always visible.
  const [pos, setPos] = useState({ top: anchor.top, left: anchor.left });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, pad = 8;
    let left = Math.min(anchor.left, vw - r.width - pad);
    left = Math.max(pad, left);
    let top = anchor.top;
    if (top + r.height > vh - pad) top = Math.max(pad, vh - r.height - pad);
    setPos({ top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor.top, anchor.left, values.length, op]);

  const width = 264;

  return (
    <div ref={ref} className="ds-fpop" style={{ top: pos.top, left: pos.left, width }}>
      <div className="ds-fpop__header">
        <span className="ds-fpop__title"><i className="bi bi-funnel-fill me-1" />{column.label || column.key}</span>
        <button className="ds-fpop__x" onClick={onClose} title="Close"><i className="bi bi-x-lg" /></button>
      </div>

      <div className="ds-fpop__sort">
        <button className="ds-fpop__sortbtn" onClick={() => { onSort("asc"); onClose(); }}>
          <i className="bi bi-sort-alpha-down" /> Sort A &rarr; Z
        </button>
        <button className="ds-fpop__sortbtn" onClick={() => { onSort("desc"); onClose(); }}>
          <i className="bi bi-sort-alpha-up-alt" /> Sort Z &rarr; A
        </button>
      </div>

      <div className="ds-fpop__section">
        <div className="ds-fpop__legend">
          <i className={`bi ${isDate ? "bi-calendar-event" : "bi-sliders"} me-1`} />
          Filter by condition
        </div>
        <Form.Select size="sm" value={op} onChange={(e) => setOp(e.target.value)} className="ds-fpop__op">
          {ops.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
        </Form.Select>
        {needsVal && (
          <div className="ds-fpop__inputs">
            <Form.Control size="sm" type={inputType}
              placeholder={op === "between" ? (isDate ? "From" : "Min") : "Value"}
              value={v1} onChange={(e) => setV1(e.target.value)} />
            {op === "between" && (
              <Form.Control size="sm" type={inputType}
                placeholder={isDate ? "To" : "Max"}
                value={v2} onChange={(e) => setV2(e.target.value)} />
            )}
          </div>
        )}
        <button className="ds-fpop__apply" disabled={needsVal && !v1} onClick={applyCondition}>
          Apply condition
        </button>
      </div>

      {!isDate && (
        <>
          <div className="ds-fpop__legend ds-fpop__legend--tight">
            <i className="bi bi-check2-square me-1" />Filter by value
          </div>
          <div className="ds-fpop__search">
            <i className="bi bi-search" />
            <input autoFocus placeholder="Search values\u2026" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="ds-fpop__list">
            <label className="ds-fpop__item ds-fpop__item--all">
              <input ref={selAllRef} type="checkbox" checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} />
              <span>(Select all)</span>
              <span className="ds-fpop__count">{allValues.length}</span>
            </label>
            {shown.length === 0 && <div className="ds-fpop__empty">No matching values</div>}
            {shown.map(([v, count]) => (
              <label key={v} className="ds-fpop__item">
                <input type="checkbox" checked={working.has(v)} onChange={() => toggleValue(v)} />
                <span title={v}>{v}</span>
                <span className="ds-fpop__count">{count}</span>
              </label>
            ))}
          </div>
        </>
      )}

      <div className="ds-fpop__foot">
        <span className="ds-fpop__status">
          {isDate ? "" : allSelected ? "All shown" : noneSelected ? "None" : `${selectedCount} of ${allValues.length}`}
        </span>
        <button className="ds-fpop__btn" onClick={() => { onClear(); onClose(); }}>Clear</button>
        {!isDate && <button className="ds-fpop__btn ds-fpop__btn--primary" onClick={applyValues}>OK</button>}
      </div>
    </div>
  );
};

export default FilterPopover;
